import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { getDownloadURL, ref } from "firebase/storage";
import { storage } from "../../services/firebase";
import { IMMEDIATE_ACTIONS, LOCATION_TYPES, STATUS_LABELS, incidentTypeLabel } from "./incidentConstants";

const GREEN = [5, 122, 85];
const DARK = [31, 41, 55];
const LIGHT = [240, 253, 250];
const ESCALATION_LABELS = {
  hr_management: "HR / Management", customer: "Customer",
  police_emergency_services: "Police / Emergency Services",
  worksafe_regulatory: "WorkSafe / regulatory consideration", other: "Other",
};
const CONTRIBUTING_LABELS = {
  procedure_process: "Procedure / process", training: "Training", equipment_vehicle: "Equipment / vehicle",
  environmental_site_conditions: "Environmental / site conditions", human_factors: "Human factors",
  ppe: "PPE", communication: "Communication", other: "Other", not_identified: "Not identified",
};
const ACTION_LABELS = {
  toolbox_talk: "Toolbox talk", retraining: "Retraining",
  review_update_work_procedures: "Review / update work procedures",
  equipment_vehicle_inspection: "Equipment / vehicle inspection",
  maintenance_repair_required: "Maintenance / repair required",
  customer_follow_up: "Report / follow-up with customer",
  authority_escalation: "Report / escalate to authority", other: "Other",
  no_further_action_required: "No further action required",
};
const TREATMENT_LABELS = {
  none: "None", first_aid: "First aid", medical_treatment: "Medical treatment",
  ambulance_emergency_services: "Ambulance / emergency services", hospital: "Hospital", other: "Other",
};
const PERSON_LABELS = {
  employee: "Employee", contractor: "Contractor", customer: "Customer",
  member_public: "Member of the public", other: "Other",
};
const GENDER_LABELS = {
  woman: "Woman", man: "Man", non_binary: "Non-binary / gender diverse", unknown: "Unknown",
};
const ENVIRONMENT_LABELS = {
  spill_leak: "Spill / leak", smoke_dust: "Smoke / dust", waste_contamination: "Waste contamination",
  wildlife_affected: "Wildlife affected", other: "Other",
};

function text(value, fallback = "Not recorded") {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (value === null || value === undefined || String(value).trim() === "") return fallback;
  return String(value);
}

function labels(values, map, otherText = "") {
  const result = (values || []).map((value) => map[value] || value);
  if (otherText && (values || []).includes("other")) result.push(`Other details: ${otherText}`);
  return result.length ? result.join(", ") : "None recorded";
}

function formatTimestamp(value, includeTime = true) {
  const date = value?.toDate?.() || (value instanceof Date ? value : value ? new Date(value) : null);
  if (!date || Number.isNaN(date.getTime())) return "Not recorded";
  return new Intl.DateTimeFormat("en-AU", includeTime
    ? { dateStyle: "medium", timeStyle: "short" }
    : { dateStyle: "medium" }).format(date);
}

function formatBytes(value) {
  const bytes = Number(value || 0);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function safeFileName(value) {
  return String(value || "incident-report").replace(/[^a-z0-9._-]+/gi, "-").replace(/-+/g, "-");
}

function ensureSpace(pdf, y, needed = 18) {
  if (y + needed <= 276) return y;
  pdf.addPage();
  drawPageHeader(pdf);
  return 31;
}

function drawPageHeader(pdf) {
  pdf.saveGraphicsState();
  pdf.setFillColor(255, 255, 255); pdf.rect(0, 0, 210, 25, "F");
  pdf.restoreGraphicsState();
}

function sectionTitle(pdf, title, y, contentHeight = 0) {
  const nextY = ensureSpace(pdf, y, 16 + contentHeight);
  pdf.setFillColor(...GREEN);
  pdf.roundedRect(14, nextY, 182, 9, 2, 2, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.setTextColor(255, 255, 255);
  pdf.text(title, 18, nextY + 6.2);
  return nextY + 12;
}

function keyValueTable(pdf, rows, y) {
  autoTable(pdf, {
    startY: y,
    body: rows.map(([label, value]) => [label, text(value)]),
    theme: "grid",
    margin: { left: 14, right: 14, top: 31, bottom: 18 },
    rowPageBreak: "avoid",
    willDrawPage: () => drawPageHeader(pdf),
    styles: { font: "helvetica", fontSize: 8.5, cellPadding: 2.5, lineColor: [217, 217, 217], lineWidth: 0.2, valign: "middle" },
    columnStyles: { 0: { cellWidth: 55, fontStyle: "bold", fillColor: LIGHT, textColor: DARK }, 1: { cellWidth: 127, textColor: DARK } },
  });
  return pdf.lastAutoTable.finalY + 6;
}

function dataTable(pdf, headers, rows, y, options = {}) {
  const { styles = {}, headStyles = {}, ...tableOptions } = options;
  autoTable(pdf, {
    startY: y,
    head: [headers],
    body: rows,
    theme: "grid",
    margin: { left: 14, right: 14, top: 31, bottom: 18 },
    rowPageBreak: "avoid",
    willDrawPage: () => drawPageHeader(pdf),
    headStyles: { fillColor: GREEN, textColor: 255, fontStyle: "bold", ...headStyles },
    styles: { font: "helvetica", fontSize: 8, cellPadding: 2.2, lineColor: [217, 217, 217], lineWidth: 0.2, valign: "middle", ...styles },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    ...tableOptions,
  });
  return pdf.lastAutoTable.finalY + 6;
}

async function imageDataUrl(url) {
  if (!url || process.env.NODE_ENV === "test" || typeof Image === "undefined" || typeof document === "undefined") return null;
  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      const scale = Math.min(1, 1200 / Math.max(image.naturalWidth || 1, image.naturalHeight || 1));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
      const context = canvas.getContext("2d");
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      try { resolve(canvas.toDataURL("image/png")); } catch { resolve(null); }
    };
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

async function storedImageDataUrl(path) {
  if (!path) return null;
  try { return await imageDataUrl(await getDownloadURL(ref(storage, path))); }
  catch { return null; }
}

async function addSignatures(pdf, incident, y) {
  const employeePath = incident.employeeDeclaration?.signatureStoragePath;
  const managerPath = incident.management?.signatureStoragePath;
  if (!employeePath && !managerPath) return y;
  const nextY = sectionTitle(pdf, "Signatures", y, 42);
  const [employeeImage, managerImage] = await Promise.all([
    storedImageDataUrl(employeePath), storedImageDataUrl(managerPath),
  ]);
  const cards = [
    { x: 14, title: "Reporting employee", image: employeeImage, detail: formatTimestamp(incident.employeeDeclaration?.signedAt) },
    { x: 107, title: "Reviewing manager", image: managerImage, detail: formatTimestamp(incident.management?.closedAt || incident.closedAt) },
  ];
  cards.forEach((card) => {
    pdf.setDrawColor(217, 217, 217);
    pdf.setFillColor(255, 255, 255);
    pdf.roundedRect(card.x, nextY, 89, 36, 2, 2, "FD");
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(8.5); pdf.setTextColor(...DARK);
    pdf.text(card.title, card.x + 4, nextY + 6);
    if (card.image) pdf.addImage(card.image, "PNG", card.x + 4, nextY + 8, 50, 17, undefined, "FAST");
    else { pdf.setFont("helvetica", "italic"); pdf.setTextColor(107, 114, 128); pdf.text("Signature stored with incident record", card.x + 4, nextY + 17); }
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(7.5); pdf.setTextColor(75, 85, 99);
    pdf.text(card.detail, card.x + 4, nextY + 31);
  });
  return nextY + 42;
}

function addPageFooters(pdf) {
  const pages = pdf.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    pdf.setPage(page);
    pdf.saveGraphicsState();
    pdf.setDrawColor(209, 213, 219);
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(7.5); pdf.setTextColor(107, 114, 128);
    pdf.line(14, 283, 196, 283);
    pdf.text(`Generated ${new Date().toLocaleString("en-AU")}`, 14, 289);
    pdf.text(`Page ${page} of ${pages}`, 196, 289, { align: "right" });
    pdf.restoreGraphicsState();
  }
}

export async function buildIncidentPdf({ incident, actions = [], attachments = [], history = [] }) {
  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait", compress: true });
  drawPageHeader(pdf);
  const partA = incident.partA || {};
  const section1 = partA.section1 || {};
  const section2 = partA.section2 || {};
  const section4 = partA.section4 || {};
  const section5 = partA.section5 || {};
  const management = incident.management || {};
  const assignment = incident.assignment || {};
  let y = 32;

  pdf.setFont("helvetica", "bold"); pdf.setFontSize(20); pdf.setTextColor(...DARK);
  pdf.text("Incident Report", 14, y);
  y += 7;
  pdf.setFontSize(11); pdf.setTextColor(...GREEN);
  pdf.text(incident.incidentNumber || "Incident draft", 14, y);
  y += 8;
  y = keyValueTable(pdf, [
    ["Report status", STATUS_LABELS[incident.workflowStatus] || incident.workflowStatus],
    ["Incident number", incident.incidentNumber || "Not assigned"],
    ["Created", formatTimestamp(incident.createdAt)],
    ["Submitted", formatTimestamp(incident.submittedAt)],
    ["Closed", formatTimestamp(incident.closedAt)],
  ], y);

  y = sectionTitle(pdf, "Part A  Person reporting", y, 25);
  y = keyValueTable(pdf, [
    ["Reporter name", incident.reporter?.name || incident.reporter?.email],
    ["Reporter position", incident.reporter?.position],
    ["Reporting manager", assignment.selectedManagerName || assignment.selectedManagerEmail],
    ["Incident date", section1.occurredDate], ["Incident time", section1.occurredTime],
    ["Location type", LOCATION_TYPES.find((item) => item.value === section1.locationType)?.label || section1.locationType],
    ["Incident address", section1.address], ["Incident type", incidentTypeLabel(section2.incidentType)],
  ], y);

  const vehicle = section2.typeDetails?.vehicle || {};
  if (section2.incidentType === "vehicle_accident") {
    y = sectionTitle(pdf, "Vehicle accident details", y, 20);
    y = keyValueTable(pdf, [
      ["Greenspace vehicle", vehicle.greenspaceRegistration], ["Other vehicle involved", vehicle.otherVehicleInvolved],
      ["Other driver or name", vehicle.otherDriverName], ["Other registration", vehicle.otherRegistration],
      ["Other contact details", vehicle.otherContact], ["Anything damaged", vehicle.anythingDamaged],
      ["Damage description", vehicle.damageDescription], ["Police contacted", vehicle.policeContacted],
    ], y);
  }
  const environmental = section2.typeDetails?.environmental || {};
  if (section2.incidentType === "environmental_spill_leak") {
    y = sectionTitle(pdf, "Environmental incident details", y, 20);
    y = keyValueTable(pdf, [
      ["What occurred", ENVIRONMENT_LABELS[environmental.eventType] || environmental.eventType],
      ["Substance or material", environmental.substance], ["Contained", environmental.contained],
      ["Environmental impact", environmental.impactDescription],
    ], y);
  }
  if (section2.incidentType === "injury_illness") {
    y = sectionTitle(pdf, "Injury notification", y, 14);
    y = keyValueTable(pdf, [["Someone injured or affected", section2.typeDetails?.injury?.someoneInjuredOrAffected]], y);
  }

  y = sectionTitle(pdf, "Description of incident", y, 28);
  y = keyValueTable(pdf, [["What happened", partA.section3?.description]], y);
  y = sectionTitle(pdf, "Immediate actions taken", y, 16);
  y = keyValueTable(pdf, [["Actions", labels(section4.selected, Object.fromEntries(IMMEDIATE_ACTIONS), section4.otherText)]], y);
  y = sectionTitle(pdf, "Witnesses and supporting evidence", y, 22);
  y = keyValueTable(pdf, [["Witnesses present", section5.witnessesPresent]], y);
  if ((section5.witnesses || []).length) {
    y = dataTable(pdf, ["Name", "Contact number", "Email"], section5.witnesses.map((item) => [text(item.name), text(item.phone), text(item.email)]), y);
  }
  if (attachments.length) {
    y = dataTable(pdf, ["Evidence file", "Type", "Size"], attachments.map((item) => [item.fileName, item.category === "photo" ? "Photo" : "Document", formatBytes(item.sizeBytes)]), y, {
      columnStyles: { 0: { cellWidth: 115, textColor: GREEN }, 1: { cellWidth: 35 }, 2: { cellWidth: 32 } },
      didDrawCell: (data) => {
        const attachment = attachments[data.row.index];
        if (data.section === "body" && data.column.index === 0 && attachment?.downloadURL) {
          pdf.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url: attachment.downloadURL });
        }
      },
    });
  } else y = keyValueTable(pdf, [["Evidence files", "None attached"]], y);

  y = sectionTitle(pdf, "Part B  Manager review", y, 25);
  y = keyValueTable(pdf, [
    ["Manager name", management.reviewingManager?.name || management.reviewingManager?.email || assignment.selectedManagerName],
    ["Manager position", management.reviewingManager?.position || assignment.selectedManagerPosition],
    ["Date reviewed", formatTimestamp(management.reviewedAt, false)],
    ["Further investigation required", management.furtherInvestigationRequired],
    ["Immediate escalation required", management.immediateEscalationRequired],
    ["Escalated to", labels(management.escalationTargets, ESCALATION_LABELS, management.escalationOtherText)],
  ], y);

  if ((management.affectedPeople || []).length) {
    y = sectionTitle(pdf, "Affected people", y, 32);
    y = dataTable(pdf, ["Person", "Contact", "Injury illness or outcome", "Treatment"], management.affectedPeople.map((person) => [
      `${PERSON_LABELS[person.personType] || person.personType}: ${text(person.name)}`,
      [person.phone, person.email, GENDER_LABELS[person.gender], person.dateOfBirth].filter(Boolean).join(" | ") || "Not recorded",
      text(person.outcomeDetails), labels(person.treatments, TREATMENT_LABELS, person.treatmentOtherText),
    ]), y, { columnStyles: { 0: { cellWidth: 38 }, 1: { cellWidth: 48 }, 2: { cellWidth: 56 }, 3: { cellWidth: 40 } } });
  }

  y = sectionTitle(pdf, "Manager review and investigation", y, 25);
  y = keyValueTable(pdf, [
    ["Manager findings", management.findings],
    ["Contributing factors", labels(management.contributingFactors, CONTRIBUTING_LABELS)],
  ], y);
  y = sectionTitle(pdf, "Corrective and follow-up actions", y, 25);
  y = keyValueTable(pdf, [
    ["Selected action types", labels(management.correctiveActionTypes, ACTION_LABELS)],
    ["Completion confirmation", management.correctiveActionsCompleted === "no_action_required" ? "No action required" : management.correctiveActionsCompleted === "yes" ? "Yes" : management.correctiveActionsCompleted === "no" ? "No" : management.correctiveActionsCompleted],
  ], y);
  if (actions.length) {
    y = dataTable(pdf, ["Action", "Assigned to", "Assigned", "Due", "Status"], actions.map((item) => [
      `${ACTION_LABELS[item.actionType] || item.actionType}\n${text(item.description)}`,
      text(item.assignedToName), text(item.assignedDate), text(item.dueDate, "N/A"), text(item.status),
    ]), y, { columnStyles: { 0: { cellWidth: 76 }, 1: { cellWidth: 34 }, 2: { cellWidth: 25 }, 3: { cellWidth: 25 }, 4: { cellWidth: 22 } } });
  }
  y = sectionTitle(pdf, "Close incident report", y, 25);
  y = keyValueTable(pdf, [
    ["Final comments", management.finalComments], ["Incident status", STATUS_LABELS[incident.workflowStatus] || incident.workflowStatus],
    ["Closed by", management.closedByEmail || management.reviewingManager?.name || management.reviewingManager?.email],
    ["Date closed", formatTimestamp(incident.closedAt)],
  ], y);
  y = await addSignatures(pdf, incident, y);

  y = sectionTitle(pdf, "Version history", y, 28);
  const orderedHistory = [...history].sort((a, b) => (a.occurredAt?.toMillis?.() || 0) - (b.occurredAt?.toMillis?.() || 0));
  if (orderedHistory.length) {
    dataTable(pdf, ["Date and time", "User", "Activity"], orderedHistory.map((event) => [
      formatTimestamp(event.occurredAt), event.actor?.name || event.actor?.email || "System",
      event.summary || event.eventType,
    ]), y, { styles: { fontSize: 7.5, cellPadding: 1.9 }, columnStyles: { 0: { cellWidth: 42 }, 1: { cellWidth: 45 }, 2: { cellWidth: 95 } } });
  } else keyValueTable(pdf, [["History", "No history events recorded"]], y);

  addPageFooters(pdf);
  return pdf;
}

export async function downloadIncidentPdf(report) {
  const pdf = await buildIncidentPdf(report);
  const { incident } = report;
  pdf.save(`${safeFileName(incident.incidentNumber || incident.id || "incident-report")}.pdf`);
}
